import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { utilizadoresAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Loader2, Save, UserCog } from 'lucide-react';
import Swal from 'sweetalert2';

export const UtilizadorForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  usePageTitle(isEdit ? 'Editar Utilizador' : 'Novo Utilizador');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    password: '',
    ativo: true
  });

  useEffect(() => {
    if (isEdit) {
      fetchUtilizador();
    }
  }, [id]);

  const fetchUtilizador = async () => {
    try {
      const response = await utilizadoresAPI.getOne(id);
      setFormData({
        nome: response.data.nome,
        email: response.data.email,
        password: '',
        ativo: response.data.ativo
      });
    } catch (error) {
      console.error('Error fetching utilizador:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Utilizador não encontrado.',
        confirmButtonColor: '#0D9488'
      });
      navigate('/utilizadores');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo obrigatório',
        text: 'O nome é obrigatório.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    if (!formData.email.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo obrigatório',
        text: 'O email é obrigatório.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    if (!isEdit && !formData.password) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo obrigatório',
        text: 'A password é obrigatória para novos utilizadores.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...formData };
      if (isEdit && !dataToSend.password) {
        delete dataToSend.password;
      }

      if (isEdit) {
        await utilizadoresAPI.update(id, dataToSend);
        Swal.fire({
          icon: 'success',
          title: 'Atualizado!',
          text: 'Utilizador atualizado com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        await utilizadoresAPI.create(dataToSend);
        Swal.fire({
          icon: 'success',
          title: 'Criado!',
          text: 'Utilizador criado com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      }
      navigate('/utilizadores');
    } catch (error) {
      console.error('Error saving utilizador:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.response?.data?.detail || 'Não foi possível guardar o utilizador.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="utilizador-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/utilizadores">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            {isEdit ? 'Editar Utilizador' : 'Novo Utilizador'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Atualize os dados do utilizador' : 'Crie um novo acesso à plataforma'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card className="border-slate-200 max-w-xl">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <UserCog className="h-5 w-5 text-amber-600" />
            Dados do Utilizador
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="nome" className="text-slate-700">
                Nome <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                placeholder="Nome completo"
                data-testid="utilizador-nome-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@exemplo.pt"
                data-testid="utilizador-email-input"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">
                Password {!isEdit && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isEdit ? 'Deixe vazio para manter a atual' : '••••••••'}
                data-testid="utilizador-password-input"
              />
              {isEdit && (
                <p className="text-xs text-slate-500">
                  Deixe em branco para manter a password atual
                </p>
              )}
            </div>

            {/* Ativo */}
            <div className="flex items-center justify-between py-4 px-4 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="ativo" className="text-slate-700 font-medium">
                  Utilizador Ativo
                </Label>
                <p className="text-sm text-slate-500">
                  Utilizadores inativos não podem fazer login
                </p>
              </div>
              <Switch
                id="ativo"
                checked={formData.ativo}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ativo: checked }))}
                data-testid="utilizador-ativo-switch"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 pt-4">
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={loading}
                data-testid="utilizador-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    A guardar...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEdit ? 'Atualizar' : 'Criar'} Utilizador
                  </>
                )}
              </Button>
              <Link to="/utilizadores">
                <Button type="button" variant="outline">
                  Cancelar
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
